import { Box, Button, Flex, Table, Tbody, Td, Thead, Tr, useToast } from '@chakra-ui/react'
import { Add, Citizen, Close, External, Group, Key, User } from '../../icons'
import { Card, CardBody } from '../../components/Card'
import { UserHeader } from './containers/UserHeader'
import { NumbersTitle, NumbersWrapper, UserNumbersTable } from './containers/NumbersTable'
import { UserDetailItemLabel, UserDetailItemValue } from './containers/UserDetailItem'
import { PageContent } from '../PageContent'
import { useParams } from 'react-router-dom'
import { useGetUserId } from '../../model/api/user/useGetUserId'
import { Loading } from '../../components/Loading'
import React, { useEffect } from 'react'
import { createAddressFromOptionalBits } from '../../model/utils/addressUtils'
import { useGetUserCurrent } from '../../model/api/user/useGetUserCurrent'
import { UserTypeEnum } from '../../model/enums/UserTypeEnum'
import { UserRoleEnum } from '../../model/enums/UserRoleEnum'
import moment from 'moment/moment'
import { LastChangeInformation } from '../../components/LastChangeInformation'
import { OrgInfoSectionHeading } from '../flexiGroup/FlexiGroupDetail'
import { PageToolbar } from '../PageToolbar'
import { useGetFlexiGroupPagination } from '../../model/api/flexiGroup/useGetFlexiGroupPagination'
import { FLEXI_GROUP_LIST_PAGE_LIMIT } from '../../pages/flexiGroup/constants'
import { usePostFlexiGroupAddMember } from '../../model/api/flexiGroup/usePostFlexiGroupAddMember'
import { usePostFlexiGroupRemoveMember } from '../../model/api/flexiGroup/usePostFlexiGroupRemoveMember'
import { usePostAddFlexiGroup } from '../../model/api/user/usePostAddFlexiGroup'
import { usePostRemoveFlexiGroup } from '../../model/api/user/usePostRemoveFlexiGroup'
import { useIsSpecialUser, useIsSupervisorOrAdmin } from '../../model/utils/userUtils'

export const UserDetail = () => {
  const { id } = useParams<'id'>() as {id: string};
  const toastMessage = useToast()
  const { data, isLoading, isError, error } = useGetUserId(id)
  const fullName = `${data?.data.firstName} ${data?.data.lastName}`
  const address = createAddressFromOptionalBits(
    data?.data.city,
    data?.data.zip,
    data?.data.street,
    data?.data.streetNumber,
  )
  const insuranceAllowed = data ? data?.data.isAllowedInsurance : false
  const status = data ? data?.data.isActive : false
  const { data: currentUserData } = useGetUserCurrent()
  useEffect(() => {
    isError &&
      toastMessage({
        title: 'Došlo k chybě',
        description: error + '! Please try again!',
        status: 'error',
        isClosable: true,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  const { data: flexiData, isLoading: flexiIsLoading, isError: flexiIsError, error: flexiError, refetch: flefiRefetch } = useGetFlexiGroupPagination(
    1,
    FLEXI_GROUP_LIST_PAGE_LIMIT,
    1,
    undefined
  )

  const isSupervisor = useIsSupervisorOrAdmin()
  const isSpecialUser = data?.data.role === UserRoleEnum.SPECIAL_USER
  const isCurrent = currentUserData?.data.id === data?.data.id;
  const isEditable = isCurrent || isSupervisor

  const { mutate: mutateAdd } = usePostAddFlexiGroup(() => {}, () => {})
  const { mutate: mutateRemove } = usePostRemoveFlexiGroup(() => {}, () => {})

  const addFlexi = (flexiGroupId: string) => {
    mutateAdd({
      flexiGroupId,
      memberId: id
    });
  }

  const removeFlexi = (flexiGroupId: string) => {
    mutateRemove({
      flexiGroupId,
      memberId: id
    });
  }

  return (
    <>
      {isLoading || !data?.data ? (
        <Loading />
      ) : (
        <PageContent>
          <UserHeader
            fullName={fullName}
            email={data?.data.email}
            phone={data?.data.phone}
            address={address}
            status={status}
            insuranceAllowed={insuranceAllowed}
            isCurrent={isCurrent}
            canResetPassword={data?.data.userType !== UserTypeEnum.LDAP}
            parentId={data?.data.parent?.id}
          />

          <Card>
            <CardBody>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { base: '1fr', lg: '21.25rem 1fr 1fr' },
                  alignItems: 'start',
                  columnGap: 8,
                  rowGap: 4,
                }}
              >
                <div>
                  <Table variant="unstyled">
                    <Tbody sx={{ '& > td': { mb: 2 } }}>
                      <Tr>
                        <td>
                          <UserDetailItemLabel>
                            <Group />
                            Organizace
                          </UserDetailItemLabel>
                        </td>
                        <td>
                          <UserDetailItemValue>{data?.data.parent?.username}</UserDetailItemValue>
                        </td>
                      </Tr>
                      <Tr>
                        {data?.data.role !== UserRoleEnum.SALESMAN && (
                          <>
                            <td>
                              <UserDetailItemLabel>
                                <User />
                                Typ uživatele
                              </UserDetailItemLabel>
                            </td>
                            <td>
                              <UserDetailItemValue>
                                {data?.data.userType?.toUpperCase()}
                              </UserDetailItemValue>
                            </td>
                          </>
                        )}
                      </Tr>
                      <Tr>
                        <td>
                          <UserDetailItemLabel>
                            <Key />
                            Role
                          </UserDetailItemLabel>
                        </td>
                        <td>
                          <UserDetailItemValue>
                            {data?.data.role === UserRoleEnum.SALESMAN
                              ? 'OBCHODNÍK'
                              : data?.data.role?.toUpperCase()}
                          </UserDetailItemValue>
                        </td>
                      </Tr>
                      {data?.data.role !== UserRoleEnum.SALESMAN && (
                        <Tr>
                          <td>
                            <UserDetailItemLabel>
                              <Citizen />
                              Uživatelské jméno
                            </UserDetailItemLabel>
                          </td>
                          <td>
                            <UserDetailItemValue>{data?.data.username}</UserDetailItemValue>
                          </td>
                        </Tr>
                      )}
                      {data?.data.usernameOtherSystem && (
                        <Tr>
                          <td>
                            <UserDetailItemLabel>
                              <External />
                              Uživatelské jméno
                              <br />
                              do&nbsp;jiných systémů
                            </UserDetailItemLabel>
                          </td>
                          <td>
                            <UserDetailItemValue>
                              {data?.data.usernameOtherSystem}
                            </UserDetailItemValue>
                          </td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                  {data?.data.updatedBy && moment(data?.data.updatedAt).isValid() && (
                    <Box mt={8}>
                      <LastChangeInformation
                        updatedBy={data?.data.updatedBy}
                        updatedAt={data?.data.updatedAt}
                      />
                    </Box>
                  )}
                </div>

                {data?.data.role !== UserRoleEnum.SALESMAN && data?.data.personalNumbers && (
                  <NumbersWrapper>
                    <NumbersTitle>Osobní čísla</NumbersTitle>

                    <UserNumbersTable rows={data.data.personalNumbers} />
                  </NumbersWrapper>
                )}
              </Box>
            </CardBody>
          </Card>

          {isSpecialUser && (
            <>
              <Box sx={{ display: 'grid', gap: 4 }}>
                <PageToolbar
                  leftElements={<OrgInfoSectionHeading>Přidělené flexibilní skupiny</OrgInfoSectionHeading>}
                />
                <Table variant="striped">
                  <Thead>
                    <Tr>
                      <Td>Název</Td>
                      <Td>Popis</Td>
                      <Td width={1}></Td>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {flexiData?.data
                      .filter(flexiGroup => flexiGroup.advisors.map(_v => _v.id).includes(parseInt(id)))
                      .map((flexiGroup) => {
                        return (
                          <Tr>
                            <Td>{flexiGroup.name}</Td>
                            <Td>{flexiGroup.description}</Td>
                            <Td>
                              <Button variant="ghost" colorScheme="black" size={'sm'} onClick={() => removeFlexi(flexiGroup.id.toString())}>
                                <Close />
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })}
                  </Tbody>
                </Table>
              </Box>

              <Box sx={{ display: 'grid', gap: 4 }}>
                <PageToolbar
                  leftElements={<OrgInfoSectionHeading>Přidat skupinu</OrgInfoSectionHeading>}
                />
                <Table variant="striped">
                  <Thead>
                    <Tr>
                      <Td>Název</Td>
                      <Td>Popis</Td>
                      <Td width={1}></Td>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {flexiData?.data
                      .filter(flexiGroup => !flexiGroup.advisors.map(_v => _v.id).includes(parseInt(id)))
                      .map((flexiGroup) => {
                        return (
                          <Tr>
                            <Td>{flexiGroup.name}</Td>
                            <Td>{flexiGroup.description}</Td>
                            <Td>
                              <Button variant="ghost" colorScheme="black" size={'sm'} onClick={() => addFlexi(flexiGroup.id.toString())}>
                                <Add />
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })}
                  </Tbody>
                </Table>
              </Box>
            </>
          )}
        </PageContent>
      )}
    </>
  )
}
